/**
 * Noticing when the television is no longer showing us (TIZEN-15, points 1-3).
 *
 * MEASURED ON A PANEL 2026-08-12: pressing Exit did not close Foyer — it
 * switched the TV's SOURCE, and the app kept running behind an HDMI input.
 * Home and Settings behave the same way and are handled by the firmware before
 * any application sees them.
 *
 * That is worse than a crash, because everything downstream still looks
 * healthy: the page is alive, the heartbeat keeps going out, and the fleet
 * console shows a green device in a room where the guest is staring at "No
 * signal". Nothing in the system could tell the difference.
 *
 * ## Foyer comes back to the front
 *
 * Ruled by Sigge, 2026-08-12: **always Foyer at front.** So an unprompted
 * departure is reclaimed — `tizen.application.launch(<our own id>)`, which is
 * the one entry point a Tizen web app has to pull itself forward.
 *
 * That is a loaded gun, and every guard below is what keeps it pointed away
 * from the guest. `kiosk-shell-remote-and-foreground` paid for these on
 * Android:
 *
 *  - **A departure we caused is never reversed.** A `launchApp`, an operator's
 *    input switch, a firmware update: all legitimate. Reversing them turns
 *    "the guest chose Netflix" into a television that yanks itself back two
 *    seconds later, forever. Only losses this module did not expect are
 *    reclaimed.
 *  - **Never wake a dark room.** A muted panel is a room somebody is asleep in,
 *    or a screen a schedule parked. Reclaiming there would light it up. The
 *    panel-mute state is read from the hardware before every attempt, and a
 *    read that fails is treated as "dark" — the safe direction.
 *  - **A ceiling, and a backoff.** A panel we cannot win back must not be
 *    fought indefinitely: that is a loop that burns the CPU of a television
 *    somebody is trying to watch. After MAX_RECLAIMS consecutive failures we
 *    stop and let the report carry the fact instead.
 *  - **The counters keep running either way**, so "how often does this happen,
 *    and did we get it back?" stays answerable from a desk.
 */

// Deliberately imports NOTHING from foyerBridge. The bridge calls
// `expectForegroundLoss()` when it hands the screen away, so importing back the
// other way would make a cycle - and a cycle between a watchdog and the module
// it watches is exactly the kind that only misbehaves at module-init time on
// the one platform we cannot debug. The platform is already on the heartbeat
// beside this field, so nothing here needs to ask for it.

/** One period during which the panel was not showing Foyer. */
export interface ForegroundLoss {
  /** When we noticed, ISO. */
  at: string;
  /** How long we were away, ms. Absent while still away. */
  ms?: number;
  /** True when the app itself asked for the screen (a launch, an input switch). */
  expected: boolean;
}

const MAX_EVENTS = 10;

/**
 * How many times running we will try to take the screen back before giving up.
 *
 * A panel we cannot win is not a panel to keep fighting: that is a loop burning
 * the CPU of a television somebody is trying to watch, and the honest outcome
 * is a report saying we lost rather than a TV that stutters. The counter resets
 * the moment a reclaim succeeds.
 */
const MAX_RECLAIMS = 3;

/** First attempt is nearly immediate; the rest back away. */
const RECLAIM_DELAYS_MS = [700, 3_000, 10_000];

let losses: ForegroundLoss[] = [];
let awaySince: number | null = null;
let expectedUntil = 0;
let installed = false;
let reclaims = 0;
let reclaimTimer: number | null = null;
/** Set once we learn this host has no way to pull itself forward at all. */
let reclaimUnavailable = false;

/**
 * Declare that WE are about to give up the screen, so the departure that
 * follows is not reported as a fault. The window is generous because the gap
 * between asking and the firmware acting is not ours to measure.
 */
export function expectForegroundLoss(windowMs = 15_000): void {
  expectedUntil = Date.now() + windowMs;
}

/** The recent losses, newest first, for the heartbeat and the probe. */
export function foregroundLosses(): ForegroundLoss[] {
  return losses;
}

/** True while the panel is showing something other than Foyer. */
export function isBackgrounded(): boolean {
  return awaySince !== null;
}

/**
 * Is the panel's backlight off? `true` also when we cannot tell.
 *
 * The unknown case is deliberately the cautious one: reclaiming into a dark
 * room is the single worst thing this module could do, and a panel that will
 * not answer `getPanelMute` is exactly the panel we should not gamble on.
 * Reading it from the hardware each time rather than caching, because a
 * schedule or an operator may have parked the screen since the last attempt.
 */
function panelIsDark(): boolean {
  try {
    const sc = (
      window as unknown as {
        webapis?: { systemcontrol?: Record<string, unknown> };
      }
    ).webapis?.systemcontrol;
    const get = sc?.["getPanelMute"];
    if (typeof get !== "function") return false; // no such concept here
    const v = (get as () => unknown).call(sc);
    if (typeof v === "boolean") return v;
    if (typeof v === "number") return v === 1;
    if (typeof v === "string") {
      const s = v.toLowerCase();
      if (s === "1" || s === "true") return true;
      if (s === "0" || s === "false") return false;
      // A string we do not recognise is NOT a lit panel. Falling through to
      // `false` here would have made every unfamiliar firmware answer mean
      // "safe to wake the room", which is the wrong direction on the one guard
      // that must never fail open.
      return true;
    }
    return true; // answered something we do not understand - assume dark
  } catch {
    return true;
  }
}

/** Our own application id, so we can ask the panel to bring us forward. */
function ownAppId(): string | null {
  try {
    const app = (
      window as unknown as {
        tizen?: {
          application?: {
            getCurrentApplication?: () => { appInfo?: { id?: string } };
          };
        };
      }
    ).tizen?.application;
    const id = app?.getCurrentApplication?.()?.appInfo?.id;
    return typeof id === "string" && id ? id : null;
  } catch {
    return null;
  }
}

/**
 * Ask the television to put Foyer back in front. Schedules itself with a
 * backoff and stops at the ceiling; every guard is documented in the module
 * header and each one is a way this can go wrong in a guest's room.
 */
function scheduleReclaim(): void {
  if (typeof window === "undefined") return;
  if (reclaimTimer !== null) return;
  if (reclaimUnavailable) return;
  if (reclaims >= MAX_RECLAIMS) return;
  const delay =
    RECLAIM_DELAYS_MS[Math.min(reclaims, RECLAIM_DELAYS_MS.length - 1)];
  reclaimTimer = window.setTimeout(() => {
    reclaimTimer = null;
    // Won it back while we waited - nothing to do, and the counter is already
    // cleared by the visible branch below.
    if (awaySince === null) return;
    if (panelIsDark()) {
      console.warn("[foreground] not reclaiming: the panel is dark");
      return;
    }
    const id = ownAppId();
    const launch = (
      window as unknown as {
        tizen?: { application?: { launch?: (...a: unknown[]) => void } };
      }
    ).tizen?.application?.launch;
    if (!id || typeof launch !== "function") {
      // A separate flag, NOT the attempt counter. Poisoning the counter to
      // stop the loop made `reclaimAttempts()` report three attempts on a host
      // that had made none - a diagnostic lying about the thing it exists to
      // measure.
      console.warn("[foreground] cannot reclaim: no self-launch on this host");
      reclaimUnavailable = true;
      return;
    }
    reclaims += 1;
    console.warn(`[foreground] reclaiming the screen (attempt ${reclaims})`);
    try {
      launch(
        id,
        () => {},
        (e: unknown) =>
          console.warn(
            `[foreground] reclaim refused: ${e instanceof Error ? e.message : String(e)}`
          )
      );
    } catch (e) {
      console.warn(`[foreground] reclaim threw: ${String(e)}`);
    }
    // Whether or not that worked, queue the next attempt. `visibilitychange`
    // firing visible is what actually stops this.
    scheduleReclaim();
  }, delay);
}

function cancelReclaim(): void {
  if (reclaimTimer !== null && typeof window !== "undefined") {
    window.clearTimeout(reclaimTimer);
  }
  reclaimTimer = null;
}

function onVisibility(): void {
  try {
    const hidden = document.visibilityState === "hidden";
    const now = Date.now();
    if (hidden) {
      if (awaySince !== null) return;
      awaySince = now;
      const expected = now <= expectedUntil;
      losses = [{ at: new Date(now).toISOString(), expected }, ...losses].slice(
        0,
        MAX_EVENTS
      );
      console.warn(
        `[foreground] lost the screen (${expected ? "expected - we asked for it" : "UNPROMPTED"})`
      );
      // Only an unprompted departure is reversed. A guest who opened Netflix
      // gets to watch Netflix.
      if (!expected) scheduleReclaim();
      return;
    }
    if (awaySince === null) return;
    const ms = now - awaySince;
    awaySince = null;
    if (losses[0]) losses[0].ms = ms;
    // We are in front again, however that happened. Stop trying, and forgive
    // the failures: the ceiling counts CONSECUTIVE losses, so a panel that
    // misbehaves once an hour is not permanently written off.
    cancelReclaim();
    reclaims = 0;
    console.warn(`[foreground] back after ${Math.round(ms / 1000)}s`);
  } catch {
    // A watchdog must never be the thing that breaks the screen it watches.
  }
}

/**
 * Start watching. Idempotent and safe everywhere: `visibilitychange` is
 * universal, and on a platform that never backgrounds the app it simply never
 * fires.
 */
export function installForegroundWatch(): void {
  if (installed || typeof document === "undefined") return;
  installed = true;
  document.addEventListener("visibilitychange", onVisibility);
}

/** Test seam. */
export function uninstallForegroundWatch(): void {
  if (!installed) return;
  installed = false;
  losses = [];
  awaySince = null;
  expectedUntil = 0;
  cancelReclaim();
  reclaims = 0;
  reclaimUnavailable = false;
  document.removeEventListener("visibilitychange", onVisibility);
}

/** How many consecutive reclaim attempts have been made. For tests + the probe. */
export function reclaimAttempts(): number {
  return reclaims;
}

/**
 * The one line the heartbeat carries: how often this panel has lost the screen
 * without us asking, and whether it is away right now.
 *
 * Only UNPROMPTED losses are counted. A panel that spent the afternoon in
 * Netflix because guests kept launching it is not a fault, and folding those in
 * would bury the signal we are actually hunting.
 */
export function foregroundReport(): string | undefined {
  const unprompted = losses.filter((l) => !l.expected);
  // Gated on the UNPROMPTED count alone, deliberately - not on "are we away
  // right now". A guest sitting in Netflix is away, legitimately, and reporting
  // that would light up the console for every television in the building doing
  // exactly what it is meant to. An unprompted departure is already in this
  // list, so nothing real is lost by staying quiet about the rest.
  if (!unprompted.length) return undefined;
  const last = unprompted[0];
  return [
    `unprompted=${unprompted.length}`,
    isBackgrounded() ? "away=now" : last?.ms ? `lastAway=${last.ms}ms` : null,
  ]
    .filter(Boolean)
    .join(" ");
}
